import { Suspense, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { SEOHead } from '@/components/layout/SEOHead'
import { ArticleLayout } from '@/components/blog/ArticleLayout'
import {
  graph,
  article as articleSchema,
  organization,
  breadcrumbList,
  faqPage,
} from '@/lib/jsonLd'
import { getArticleBySlug } from './articles'

export default function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>()
  const article = slug ? getArticleBySlug(slug) : undefined

  /*
   * Composed Schema.org graph for the article page:
   * - Article (richer than before: image, dateModified, @id-referenced author/publisher)
   * - Organization (publisher identity, resolves @id refs from Article)
   * - BreadcrumbList (Strona główna → Blog → <article title>)
   * - FAQPage (only when meta.faqs is populated — added per-article in Batch 3)
   *
   * useMemo lives BEFORE the early return so the Rules of Hooks aren't violated
   * when an unknown slug renders the not-found branch.
   */
  const articleJsonLd = useMemo(() => {
    if (!article) return undefined
    const nodes: Record<string, unknown>[] = [
      articleSchema(article),
      organization(),
      breadcrumbList([
        { name: 'Strona główna', url: '/' },
        { name: 'Blog', url: '/blog' },
        { name: article.title, url: `/blog/${article.slug}` },
      ]),
    ]
    if (article.faqs && article.faqs.length > 0) {
      nodes.push(faqPage(article.faqs))
    }
    return graph(...nodes)
  }, [article])

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <SEOHead title="Nie znaleziono artykułu" description="" slug={`/blog/${slug ?? ''}`} />
        <h1 className="text-2xl font-bold text-foreground mb-4">Nie znaleziono artykułu</h1>
        <p className="text-muted-foreground mb-6">Ten artykuł nie istnieje lub został usunięty.</p>
        <Link
          to="/blog"
          className="text-primary font-semibold hover:underline"
        >
          Wróć do bloga
        </Link>
      </div>
    )
  }

  const ArticleContent = article.component

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.description}
        slug={`/blog/${article.slug}`}
        keywords={article.keywords}
        jsonLd={articleJsonLd}
      />
      <ArticleLayout title={article.title} publishedAt={article.publishedAt} slug={article.slug}>
        <Suspense fallback={<div className="h-96 animate-pulse bg-muted/50 rounded-xl" />}>
          <ArticleContent />
        </Suspense>
      </ArticleLayout>
    </>
  )
}
